/**
 * Draggable Grid Post Group Component
 *
 * Compact post thumbnail for grid view with expandable variations
 * Main post is draggable, variations expand in mini-grid below
 */

import { useDraggable } from '@dnd-kit/core';
import { useTranslation } from 'react-i18next';
import { Post, PostVariationSummary } from '@/services/posts';
import { Icon } from '@/components/ui/Icon';
import { Tooltip } from '@/components/ui/Tooltip';
import { HoverCard } from '@/components/ui/HoverCard';
import { RetryImage, PlaceholderImage } from '@/components/ui';
import { ICONS } from '@/constants/icons';
import { cn } from '@/utils/cn';
import { getPostStatusBadge, getVariationStatusBadge } from '@/utils/calendarStatus';
import { SidebarPostPopover } from './PostPopover';
import { releaseItemKey } from '@/types/releaseSections';

/** Small "New" pill on the tile of a newly released item (calendar nudge). */
const NewBadge = ({ label }: { label: string }) => (
  <span className="absolute left-1.5 top-1.5 rounded bg-coral-500 px-1.5 py-0.5 text-[10px] font-semibold leading-none text-coral-contrast shadow-sm">
    {label}
  </span>
);

interface DraggableGridPostGroupProps {
  post: Post;
  isSelected: boolean;
  selectedVariationId?: number;
  isExpanded?: boolean;
  onToggleExpand?: () => void;
  onClick: () => void;
  onVariationClick?: (variation: PostVariationSummary) => void;
  onEdit?: () => void;
  onDelete?: () => void;
  onEditVariation?: (variation: PostVariationSummary) => void;
  onDeleteVariation?: (variation: PostVariationSummary) => void;
  /** Exact items released since the last visit (`<postId>:base` / `<postId>:<variationId>`) */
  newItemKeys?: ReadonlySet<string>;
}

/**
 * Get content status badge (DRAFT or READY)
 */
function getContentStatusBadge(
  contentStatus: 'DRAFT' | 'READY' | undefined,
  t: (key: string, options?: { defaultValue?: string }) => string
): { color: string; bgColor: string; label: string; emoji: string } | null {
  if (contentStatus === 'DRAFT') {
    return {
      color: 'text-amber-700',
      bgColor: 'bg-amber-100',
      label: t('draft', { defaultValue: 'Draft' }),
      emoji: '✏️'
    };
  }
  if (contentStatus === 'READY') {
    return {
      color: 'text-green-700',
      bgColor: 'bg-green-100',
      label: t('ready_to_shoot', { defaultValue: 'Ready to Shoot' }),
      emoji: '🚀'
    };
  }
  return null;
}

export const DraggableGridPostGroup = ({
  post,
  isSelected,
  selectedVariationId,
  isExpanded,
  onToggleExpand,
  onClick,
  onVariationClick,
  onEdit,
  onDelete,
  onEditVariation,
  onDeleteVariation,
  newItemKeys,
}: DraggableGridPostGroupProps) => {
  const { t } = useTranslation('common');
  const { t: tCal } = useTranslation('calendar');
  // The tile stands for the whole group: it is marked when the original or any version is new
  const isGroupNew =
    (newItemKeys?.has(releaseItemKey(post.id, null)) ?? false) ||
    (post.variations?.some((variation) => newItemKeys?.has(releaseItemKey(post.id, variation.id))) ?? false);

  const { attributes, listeners, setNodeRef, isDragging } = useDraggable({
    id: `sidebar-post-${post.id}`,
    data: { type: 'sidebar-post', post },
  });

  // Get first media item if available
  const firstMedia = post.postMedia?.[0];
  const hasMedia = firstMedia && (firstMedia.thumbnailUrl || firstMedia.originalUrl);

  const contentStatus = getContentStatusBadge(post.contentStatus, t);
  const inferredStatus = getPostStatusBadge(post, t);

  // Check if post has variations
  const variationsCount = post.variations?.length ?? 0;
  const hasVariations = variationsCount > 0;

  // Main post card (inner content)
  const postCardInner = (
    <div
      ref={setNodeRef}
      {...listeners}
      {...attributes}
      onClick={onClick}
      className={cn(
        'group relative aspect-square rounded-xl overflow-hidden',
        'cursor-grab active:cursor-grabbing',
        'transition-all duration-300 ease-out',
        'bg-gray-100 touch-none',
        isSelected ? 'ring-2 ring-primary-500 ring-offset-2' : '',
        isDragging ? 'opacity-50 scale-90 rotate-2' : 'hover:scale-[1.08] hover:shadow-xl hover:z-10'
      )}
      role="button"
      tabIndex={0}
      aria-label={t('aria_post_label', {
        caption: post.caption?.substring(0, 50) || t('no_caption', { defaultValue: 'No caption' }),
        defaultValue: 'Post: {{caption}}',
      })}
      onKeyDown={(e) => {
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault();
          onClick();
        }
      }}
    >
      {/* Thumbnail */}
      {hasMedia ? (
        <RetryImage
          src={firstMedia.thumbnailUrl || firstMedia.originalUrl || undefined}
          alt=""
          className="w-full h-full object-cover"
          loading="lazy"
        />
      ) : (
        <PlaceholderImage />
      )}

      {/* Gradient overlay at bottom */}
      <div className="absolute inset-x-0 bottom-0 h-8 bg-gradient-to-t from-black/50 to-transparent" />

      {isGroupNew && <NewBadge label={tCal('release_new_badge')} />}

      {/* Status badges - top right */}
      <div className="absolute top-1.5 right-1.5 flex items-center gap-1">
        {inferredStatus ? (
          <Tooltip content={inferredStatus.label}>
            <span className="w-6 h-6 flex items-center justify-center bg-white/90 backdrop-blur-sm rounded-md shadow-sm">
              <Icon name={inferredStatus.icon} size="sm" className={inferredStatus.color} ariaHidden />
            </span>
          </Tooltip>
        ) : (
          <Tooltip content={t('status_unmapped', { defaultValue: 'Not on calendar' })}>
            <span className="w-6 h-6 flex items-center justify-center bg-white/90 backdrop-blur-sm rounded-md shadow-sm">
              <Icon name={ICONS.CALENDAR} size="sm" className="text-gray-400" ariaHidden />
            </span>
          </Tooltip>
        )}
      </div>

      {/* Content status badge - bottom left (hidden for testing) */}
      {/* {contentStatus && (
        <div className="absolute bottom-1.5 left-1.5">
          <Tooltip content={contentStatus.label}>
            <span className={cn("w-6 h-6 text-xs font-medium rounded-sm flex items-center justify-center shadow-sm", contentStatus.bgColor, contentStatus.color)}>
              {contentStatus.emoji}
            </span>
          </Tooltip>
        </div>
      )} */}

      {/* Variation count badge - bottom right */}
      {hasVariations && (
        <Tooltip content={isExpanded ? t('collapse_variations', { defaultValue: 'Click to collapse variations' }) : t('expand_variations', { defaultValue: 'Click to expand variations' })}>
          <button
            onClick={(e) => {
              e.stopPropagation();
              onToggleExpand?.();
            }}
            className="absolute bottom-1.5 right-1.5 w-8 h-8 flex items-center justify-center bg-secondary-600 text-secondary-600-contrast text-xs font-bold rounded-md shadow-md hover:bg-secondary-700 transition-colors"
            aria-label={isExpanded ? t('collapse_variations') : t('expand_variations')}
          >
            {isExpanded ? '×' : variationsCount}
          </button>
        </Tooltip>
      )}
    </div>
  );

  // Main post card wrapped with HoverCard
  const postCard = (
    <HoverCard
      trigger={postCardInner}
      side="right"
      align="start"
      sideOffset={8}
      open={isDragging ? false : undefined}
      allowTriggerPointerEvents
      mobileSheetWithTriggerEvents
    >
      <SidebarPostPopover
        post={post}
        contentStatus={contentStatus}
        inferredStatus={inferredStatus}
        onEdit={onEdit}
        onDelete={onDelete}
      />
    </HoverCard>
  );

  // If not expanded or no variations, just return the post card
  if (!isExpanded || !hasVariations) {
    return postCard;
  }

  // When expanded, return post + variations as siblings in Fragment
  return (
    <>
      {postCard}
      {post.variations?.map((variation) => (
        <DraggableGridVariationCard
          key={variation.id}
          variation={variation}
          parentPost={post}
          isSelected={selectedVariationId === variation.id}
          isNew={newItemKeys?.has(releaseItemKey(post.id, variation.id)) ?? false}
          onClick={() => onVariationClick?.(variation)}
          onEdit={() => onEditVariation?.(variation)}
          onDelete={onDeleteVariation && (post.variations?.length ?? 0) > 1 ? () => onDeleteVariation(variation) : undefined} // a post's only variation can't be deleted
        />
      ))}
    </>
  );
};

/**
 * Full-size variation card in grid (shown when parent is expanded)
 */
interface DraggableGridVariationCardProps {
  variation: PostVariationSummary;
  parentPost: Post;
  isSelected: boolean;
  isNew: boolean;
  onClick: () => void;
  onEdit?: () => void;
  onDelete?: () => void;
}

const DraggableGridVariationCard = ({
  variation,
  parentPost,
  isSelected,
  isNew,
  onClick,
  onEdit,
  onDelete,
}: DraggableGridVariationCardProps) => {
  const { t } = useTranslation('common');
  const { t: tCal } = useTranslation('calendar');
  const { attributes, listeners, setNodeRef, isDragging } = useDraggable({
    id: `sidebar-variation-${variation.id}`,
    data: {
      type: 'sidebar-variation',
      variation: { ...variation, postId: parentPost.id },
      postId: parentPost.id,
    },
  });

  // Effective values (variation's own or parent's)
  const effectiveMedia = variation.postMedia?.length ? variation.postMedia : parentPost.postMedia;
  const firstMedia = effectiveMedia?.[0];
  const hasMedia = firstMedia && (firstMedia.thumbnailUrl || firstMedia.originalUrl);

  const contentStatus = getContentStatusBadge(variation.contentStatus, t);
  const inferredStatus = getVariationStatusBadge(variation, t);

  return (
    <HoverCard
      trigger={
        <div
          ref={setNodeRef}
          {...listeners}
          {...attributes}
          onClick={onClick}
          className={cn(
            'group relative aspect-square rounded-xl overflow-hidden',
            'cursor-grab active:cursor-grabbing',
            'transition-all duration-300 ease-out',
            'bg-gray-100 touch-none ring-2 ring-secondary-300',
            isSelected ? 'ring-secondary-500 ring-offset-2' : '',
            isDragging ? 'opacity-50 scale-90 rotate-2' : 'hover:scale-[1.08] hover:shadow-xl hover:z-10'
          )}
        >
          {/* Thumbnail */}
          {hasMedia ? (
            <RetryImage
              src={firstMedia.thumbnailUrl || firstMedia.originalUrl || undefined}
              alt=""
              className="w-full h-full object-cover"
              loading="lazy"
            />
          ) : (
            <PlaceholderImage />
          )}

          {isNew && <NewBadge label={tCal('release_new_badge')} />}

          {/* Inferred status badge - top right */}
          <div className="absolute top-1.5 right-1.5">
            {inferredStatus ? (
              <Tooltip content={inferredStatus.label}>
                <span className="w-6 h-6 flex items-center justify-center bg-white/90 backdrop-blur-sm rounded-md shadow-sm">
                  <Icon name={inferredStatus.icon} size="sm" className={inferredStatus.color} ariaHidden />
                </span>
              </Tooltip>
            ) : (
              <Tooltip content={t('status_unmapped', { defaultValue: 'Not on calendar' })}>
                <span className="w-6 h-6 flex items-center justify-center bg-white/90 backdrop-blur-sm rounded-md shadow-sm">
                  <Icon name={ICONS.CALENDAR} size="sm" className="text-gray-400" ariaHidden />
                </span>
              </Tooltip>
            )}
          </div>

        </div>
      }
      side="right"
      align="start"
      sideOffset={8}
      allowTriggerPointerEvents
      mobileSheetWithTriggerEvents
    >
      <SidebarPostPopover
        post={parentPost}
        variation={variation}
        contentStatus={contentStatus}
        inferredStatus={inferredStatus}
        onEdit={onEdit}
        onDelete={onDelete}
      />
    </HoverCard>
  );
};
